import { LanguageSupport } from '@codemirror/language'
import { StreamLanguage } from '@codemirror/language'

// State interface for LaTeX tokenizer
interface LatexState {
  inMath: boolean
  inDisplayMath: boolean
  inDisplayMathDollar: boolean
  inOptionalParam: boolean
}

// Enhanced LaTeX language support for CodeMirror with improved token classification
const latexLanguage = StreamLanguage.define<LatexState>({
  name: 'latex',
  
  token(stream, state: LatexState) {
    // Comments - must be checked first
    if (stream.match(/%.*$/)) {
      return 'comment'
    }
    
    // LaTeX equation environments (begin/end)
    if (stream.match(/\\begin\{(equation|align|gather|multline|split|eqnarray)\*?\}/)) {
      state.inDisplayMath = true
      return 'keyword'
    }
    
    if (stream.match(/\\end\{(equation|align|gather|multline|split|eqnarray)\*?\}/)) {
      state.inDisplayMath = false
      return 'keyword'
    }
    
    // Document structure commands
    if (stream.match(/\\(documentclass|usepackage|document|begin|end)\b/)) {
      return 'keyword'
    }
    
    // Other LaTeX environments
    if (stream.match(/\\begin\{[^}]+\}/)) {
      return 'keyword'
    }
    
    if (stream.match(/\\end\{[^}]+\}/)) {
      return 'keyword'
    }
    
    // Math delimiters - display math
    if (stream.match(/\$\$/)) {
      state.inDisplayMathDollar = !state.inDisplayMathDollar
      return 'string'
    }
    
    // Math delimiters - inline math
    if (stream.match(/\$/)) {
      state.inMath = !state.inMath
      return 'string'
    }
    
    // LaTeX commands (backslash followed by letters)
    if (stream.match(/\\[a-zA-Z@]+\*?/)) {
      return 'keyword'
    }
    
    // Special characters in commands
    if (stream.match(/\\[^a-zA-Z@\s]/)) {
      return 'keyword'
    }
    
    // Braces - opening and closing
    if (stream.match(/[{}]/)) {
      return 'bracket'
    }
    
    // Optional parameters in square brackets
    if (stream.match(/\[/)) {
      state.inOptionalParam = true
      return 'attributeName'
    }
    
    if (state.inOptionalParam && stream.match(/\]/)) {
      state.inOptionalParam = false
      return 'attributeName'
    }
    
    // Content inside optional parameters
    if (state.inOptionalParam) {
      stream.next()
      return 'string'
    }
    
    // Labels and references
    if (stream.match(/\\label\{[^}]*\}/)) {
      return 'atom'
    }
    
    if (stream.match(/\\ref\{[^}]*\}/) || stream.match(/\\eqref\{[^}]*\}/) || stream.match(/\\pageref\{[^}]*\}/)) {
      return 'atom'
    }
    
    // Citations
    if (stream.match(/\\cite[a-z]*\{[^}]*\}/)) {
      return 'atom'
    }
    
    // Math mode content
    if (state.inMath || state.inDisplayMath || state.inDisplayMathDollar) {
      // Math operators
      if (stream.match(/[+\-*/=<>≤≥≠±×÷∑∏∫∂∇√∞]/)) {
        return 'operator'
      }
      // Math commands
      if (stream.match(/\\[a-zA-Z@]+/)) {
        return 'keyword'
      }
      // Greek letters and math symbols
      if (stream.match(/[αβγδεζηθικλμνξοπρστυφχψωΑΒΓΔΕΖΗΘΙΚΛΜΝΞΟΠΡΣΤΥΦΧΨΩ]/)) {
        return 'variableName'
      }
      // Numbers in math
      if (stream.match(/[0-9]+(\.[0-9]+)?/)) {
        return 'number'
      }
      // Variables in math (single letters)
      if (stream.match(/[a-zA-Z]/)) {
        return 'variableName'
      }
      // Parentheses in math
      if (stream.match(/[()]/)) {
        return 'bracket'
      }
      // Other math content
      stream.next()
      return 'variableName'
    }
    
    // Regular text content
    stream.next()
    return null
  },
  
  startState(): LatexState {
    return {
      inMath: false,
      inDisplayMath: false,
      inDisplayMathDollar: false,
      inOptionalParam: false,
    }
  },
})

export function latexSupport(): LanguageSupport {
  return new LanguageSupport(latexLanguage)
}